import { __decorate, __extends } from "tslib";
import { attachShadowTemplate, coerceBoolean, coerceNumber, CustomElement, ensureChildren, FoundationProperty, upgradeProperty } from '@tyler-components-web/core';
import { tylIconCheckBox, tylIconCheckBoxOutlineBlank } from '@tylertech/tyler-icons/standard';
import { DividerComponent } from '../divider';
import { LinearProgressComponent } from '../linear-progress';
import { ListComponent, ListItemComponent } from '../list';
import { PopupComponent } from '../popup';
import { SkeletonComponent } from '../skeleton';
import { TextFieldComponent } from '../text-field';
import { AutocompleteAdapter } from './autocomplete-adapter';
import { AUTOCOMPLETE_CONSTANTS } from './autocomplete-constants';
import { AutocompleteFoundation } from './autocomplete-foundation';
import { IconComponent, TylerIconRegistry } from '../icon';
import { ListDropdownAware } from '../list-dropdown/list-dropdown-aware';
var template = require('./autocomplete.html');
var styles = require('./autocomplete.css');
/**
 * The custom element class behind the `<tcw-autocomplete>` element.
 */
var AutocompleteComponent = /** @class */ (function (_super) {
    __extends(AutocompleteComponent, _super);
    function AutocompleteComponent() {
        var _this = _super.call(this) || this;
        TylerIconRegistry.define([tylIconCheckBox, tylIconCheckBoxOutlineBlank]);
        attachShadowTemplate(_this, AUTOCOMPLETE_CONSTANTS.elementName, template, styles);
        _this._foundation = new AutocompleteFoundation(new AutocompleteAdapter(_this));
        return _this;
    }
    Object.defineProperty(AutocompleteComponent, "observedAttributes", {
        get: function () {
            return [
                AUTOCOMPLETE_CONSTANTS.attributes.MODE,
                AUTOCOMPLETE_CONSTANTS.attributes.MULTIPLE,
                AUTOCOMPLETE_CONSTANTS.attributes.DEBOUNCE,
                AUTOCOMPLETE_CONSTANTS.attributes.HIGHLIGHT_FIRST,
                AUTOCOMPLETE_CONSTANTS.attributes.FILTER_ON_FOCUS,
                AUTOCOMPLETE_CONSTANTS.attributes.ALLOW_UNMATCHED,
                AUTOCOMPLETE_CONSTANTS.attributes.POPUP_TARGET,
                AUTOCOMPLETE_CONSTANTS.attributes.POPUP_CLASSES,
                AUTOCOMPLETE_CONSTANTS.attributes.OBSERVE_SCROLL,
                AUTOCOMPLETE_CONSTANTS.attributes.OBSERVE_SCROLL_THRESHOLD,
                AUTOCOMPLETE_CONSTANTS.attributes.OPTION_LIMIT,
                AUTOCOMPLETE_CONSTANTS.attributes.SYNC_POPUP_WIDTH,
                AUTOCOMPLETE_CONSTANTS.attributes.OPEN,
                AUTOCOMPLETE_CONSTANTS.attributes.SELECT_ON_BLUR,
                AUTOCOMPLETE_CONSTANTS.attributes.MATCH_KEY
            ];
        },
        enumerable: true,
        configurable: true
    });
    AutocompleteComponent.prototype.initializedCallback = function () {
        _super.prototype.initializedCallback.call(this);
        upgradeProperty(this, 'mode');
        upgradeProperty(this, 'multiple');
        upgradeProperty(this, 'value');
        upgradeProperty(this, 'debounce');
        upgradeProperty(this, 'filterOnFocus');
        upgradeProperty(this, 'allowUnmatched');
        upgradeProperty(this, 'popupTarget');
        upgradeProperty(this, 'optionBuilder');
        upgradeProperty(this, 'filter');
        upgradeProperty(this, 'selectedTextBuilder');
        upgradeProperty(this, 'beforeValueChange');
        upgradeProperty(this, 'onBeforeValueChange');
        upgradeProperty(this, 'open');
        upgradeProperty(this, 'selectOnBlur');
        upgradeProperty(this, 'matchKey');
        upgradeProperty(this, 'optionComparator');
    };
    AutocompleteComponent.prototype.connectedCallback = function () {
        var _this = this;
        if (this.children.length) {
            this._foundation.initialize();
        }
        else {
            ensureChildren(this)
                .then(function () { return _this._foundation.initialize(); })
                .catch(function (e) { return console.error(e); });
        }
    };
    AutocompleteComponent.prototype.disconnectedCallback = function () {
        this._foundation.disconnect();
    };
    AutocompleteComponent.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
        _super.prototype.attributeChangedCallback.call(this, name, oldValue, newValue);
        switch (name) {
            case AUTOCOMPLETE_CONSTANTS.attributes.MODE:
                this.mode = newValue;
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.MULTIPLE:
                this.multiple = coerceBoolean(newValue);
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.DEBOUNCE:
                this.debounce = coerceNumber(newValue);
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.FILTER_ON_FOCUS:
                this.filterOnFocus = coerceBoolean(newValue);
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.ALLOW_UNMATCHED:
                this.allowUnmatched = coerceBoolean(newValue);
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.POPUP_TARGET:
                this.popupTarget = newValue;
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.OPEN:
                this.open = coerceBoolean(newValue);
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.SELECT_ON_BLUR:
                this.selectOnBlur = coerceBoolean(newValue);
                break;
            case AUTOCOMPLETE_CONSTANTS.attributes.MATCH_KEY:
                this.matchKey = newValue;
                break;
        }
    };
    /** Adds options to the dropdown while it is open. Has no effect if the dropdown is closed.  */
    AutocompleteComponent.prototype.appendOptions = function (options) {
        this._foundation.appendOptions(options);
    };
    /** Opens the dropdown. */
    AutocompleteComponent.prototype.openDropdown = function () {
        this.open = true;
    };
    /** Closes the dropdown. */
    AutocompleteComponent.prototype.closeDropdown = function () {
        this.open = false;
    };
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "mode", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "multiple", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "value", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "debounce", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "filterOnFocus", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "allowUnmatched", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "popupTarget", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "optionBuilder", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "filter", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "selectedTextBuilder", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "open", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "selectOnBlur", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "matchKey", void 0);
    __decorate([
        FoundationProperty({ set: false })
    ], AutocompleteComponent.prototype, "isInitialized", void 0);
    __decorate([
        FoundationProperty({ set: false })
    ], AutocompleteComponent.prototype, "popupElement", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "beforeValueChange", void 0);
    __decorate([
        FoundationProperty({ get: false, name: 'beforeValueChange' })
    ], AutocompleteComponent.prototype, "onBeforeValueChange", void 0);
    __decorate([
        FoundationProperty()
    ], AutocompleteComponent.prototype, "optionComparator", void 0);
    AutocompleteComponent = __decorate([
        CustomElement({
            name: AUTOCOMPLETE_CONSTANTS.elementName,
            dependencies: [
                TextFieldComponent,
                PopupComponent,
                ListComponent,
                ListItemComponent,
                DividerComponent,
                SkeletonComponent,
                LinearProgressComponent,
                IconComponent
            ]
        })
    ], AutocompleteComponent);
    return AutocompleteComponent;
}(ListDropdownAware));
export { AutocompleteComponent };
